/**
 * DeleteRaceModal — confirmación + DELETE de race_target (v15.5-§8.3.D).
 *
 * Política DELETE backend (§8.3.B, defensa hasta respuesta Alberto §8.2.D):
 * - is_primary + otras race_targets → cannot_delete_primary_with_other_races.
 * - is_primary única → cannot_delete_only_primary.
 *
 * UX: el botón "Borrar" del RaceCard es visible PARA TODAS las race_targets
 * (incluida la primary). Cuando el user pulsa borrar de una primary, el
 * modal se abre y al confirmar el backend rechaza con el mensaje
 * correspondiente — se muestra INLINE sin cerrar el modal, para que el
 * user lea la salida (marcar otra como principal primero — §8.3.E).
 *
 * En éxito: 204 → onDeleted (refresh padre) + onClose.
 *
 * Patrón visual idéntico al delete-modal de PlanPage.tsx: title + body
 * + cancel + confirm rojo (`bg-danger`).
 */
import { useState } from "react";

import { deleteRace } from "@/api/races";
import { t } from "@/i18n/es";
import { ApiError, type RaceTarget } from "@/types/api";

interface Props {
  race: RaceTarget;
  onClose: () => void;
  /** Llamado tras DELETE 204. Padre debe hacer refresh() de la lista. */
  onDeleted: () => void;
}

/** Mapea error codes de DELETE a mensajes UX i18n. */
function _deleteErrorMessage(code: string): string {
  switch (code) {
    case "cannot_delete_primary_with_other_races":
      return t.races.errorDeletePrimaryWithOthers;
    case "cannot_delete_only_primary":
      return t.races.errorDeleteOnlyPrimary;
    case "race_target_not_found":
    case "race_target_not_owned":
      return t.races.errorNotFoundOrOwned;
    default:
      return t.races.errorGeneric;
  }
}

/** Descripción para el body — usa name si existe, fallback a distance label. */
function _raceDescription(race: RaceTarget): string {
  if (race.name) return race.name;
  // Fallback simple: enum lowercase. El RaceCard sí mapea a label, pero
  // aquí el modal no importa el módulo de RaceCard — minimizamos
  // dependencias entre componentes hermanos.
  return race.distance;
}

export function DeleteRaceModal({ race, onClose, onDeleted }: Props) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onConfirmDelete() {
    setSubmitting(true);
    setError(null);
    try {
      await deleteRace(race.id);
      onDeleted();
      onClose();
    } catch (err) {
      // En error: mostrar mensaje inline + NO cerrar el modal (el user
      // lee la salida y decide qué hacer).
      if (err instanceof ApiError) {
        setError(_deleteErrorMessage(err.code));
      } else {
        setError(t.races.errorGeneric);
      }
      setSubmitting(false);
    }
  }

  return (
    <div
      data-testid="delete-race-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-race-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
    >
      <div className="w-full max-w-sm rounded-lg border border-border-subtle bg-carbon-raised p-5">
        <h2
          id="delete-race-modal-title"
          className="text-lg font-medium text-bone"
        >
          {t.races.deleteModalTitle}
        </h2>
        <p
          data-testid="delete-race-modal-body"
          className="mt-2 text-sm text-bone-muted"
        >
          {t.races.deleteModalBody.replace("{race}", _raceDescription(race))}
        </p>

        {error && (
          <p
            data-testid="delete-race-modal-error"
            role="alert"
            className="mt-3 text-sm text-danger"
          >
            {error}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-3">
          <button
            type="button"
            data-testid="delete-race-cancel"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md border border-border-subtle px-4 py-2 text-sm text-bone-muted hover:border-border-strong hover:text-bone disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t.races.modalCancel}
          </button>
          <button
            type="button"
            data-testid="delete-race-confirm"
            onClick={() => void onConfirmDelete()}
            disabled={submitting}
            className="rounded-md bg-danger px-4 py-2 text-sm font-medium text-white transition-opacity duration-fast hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t.races.deleteModalConfirm}
          </button>
        </div>
      </div>
    </div>
  );
}
